import { app } from '../../app';
import { CollideEvent, CollideId } from '../../defs/collide';
import type { PipeBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { InvalidEffect } from '../effects/InvalidEffect';
import { PipeGoalEffect } from '../effects/PipeGoalEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    pipe block collision resolver
*/
export class PipeCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: PipeBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: PipeBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap() {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock() {
    // trigger pipe
    void this._trigger();
    return true;
  }

  private async _resolveAttack() {
    // trigger pipe
    void this._trigger();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _trigger() {
    // play sound
    app.sound.play('puzzle-pipe-poke', {
      dupes: 2,
      pan: mapGetPan(this._entity.c.position.mapPosition),
    });

    this._scene?.events.publish({ id: 'poke' });

    const wobbleView = this._entity.c.view2d.view.getChildAt(0) ?? undefined;

    // We could await the this._trigger() instead, but that will cause the blocks wait for the animation to finish before falling.
    // This way, the animation will play in parallel, with the disadvantage that the goal count updates before the animation finishes.
    // Awaiting this without awaiting the trigger causes the game to not finish after last goal completed.
    void new PipeGoalEffect(this._scene, {
      position: this._entity.c.position.mapPosition,
      wobbleView,
    }).execute();

    // trigger goal
    this._scene.sessionSystem.triggerGoal('pipe', this._entity);
  }
}
